import { colorsPalettes } from "@similarweb/styles";

import { normalizePop } from "./normalizePop";

const percentile95 = (values: number[]): number => {
    const magnitudes = values.map(Math.abs).toSorted((left, right) => left - right);
    if (!magnitudes.length) {
        return 0;
    }
    const index = Math.min(magnitudes.length - 1, Math.ceil(magnitudes.length * 0.95) - 1);
    return magnitudes[index];
};

export interface IGrowthHeatmapStyle {
    backgroundColor: string;
    shouldUseLightText: boolean;
}

type HeatmapStop = { threshold: number; bg: string; shouldUseLightText: boolean };

const POSITIVE_STOPS: HeatmapStop[] = [
    { threshold: 0, bg: colorsPalettes.green[300], shouldUseLightText: false },
    { threshold: 0.33, bg: colorsPalettes.green["400_P"], shouldUseLightText: false },
    { threshold: 0.66, bg: colorsPalettes.green[600], shouldUseLightText: true },
];

const NEGATIVE_STOPS: HeatmapStop[] = [
    { threshold: 0, bg: colorsPalettes.red[200], shouldUseLightText: false },
    { threshold: 0.33, bg: colorsPalettes.red[300], shouldUseLightText: false },
    { threshold: 0.66, bg: colorsPalettes.red[400], shouldUseLightText: true },
];

const LIGHT_TEXT_BACKGROUNDS = new Set<string>([colorsPalettes.green[600], colorsPalettes.red[400]]);

const pickStop = (stops: HeatmapStop[], intensity: number): HeatmapStop =>
    stops.reduce((best, stop) => (intensity >= stop.threshold ? stop : best));

export const shouldUseLightHeatmapText = (backgroundColor?: string): boolean =>
    backgroundColor != null && LIGHT_TEXT_BACKGROUNDS.has(backgroundColor);

export const buildGrowthHeatmapGetStyle = (popValues: Array<number | null | undefined>) => {
    const cappedMax = percentile95(
        popValues
            .map((value) => normalizePop(value))
            .filter((value): value is number => value != null)
            .map(Math.abs),
    );

    return (pop: number | null | undefined): IGrowthHeatmapStyle => {
        const normalizedPop = normalizePop(pop);

        if (normalizedPop == null || cappedMax === 0) {
            return { backgroundColor: colorsPalettes.carbon[25], shouldUseLightText: false };
        }

        const intensity = Math.min(Math.abs(normalizedPop) / cappedMax, 1);

        if (normalizedPop > 0) {
            const stop = pickStop(POSITIVE_STOPS, intensity);
            return { backgroundColor: stop.bg, shouldUseLightText: stop.shouldUseLightText };
        }

        if (normalizedPop < 0) {
            const stop = pickStop(NEGATIVE_STOPS, intensity);
            return { backgroundColor: stop.bg, shouldUseLightText: stop.shouldUseLightText };
        }

        return { backgroundColor: colorsPalettes.carbon[100], shouldUseLightText: false };
    };
};
